import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import { DatePicker, TimePicker } from "@src/common/components";
import {
  useOverrideStopCompletedTimesMutation,
  Shipment,
} from "@api/graphql/generated/generated-types";
import CourieButton from "@src/common/components/Button/Button";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import { useAuth } from "@src/common/hooks/useAuth";
import {
  momentToTimePickerDate,
  makeTimeOnDateInCourierTZ,
  dateToTimeString,
  timeStringToDate,
} from "@src/common/utils/timezoneConverters";
import StopCard from "@src/common/components/StopCard/StopCard";
import { getFirstTaskSequence } from "@src/shipments/utils/typeConvertors";

type StopTimeFields = {
  date: Date | null;
  time: Date | null;
};

type StopCompletedTimesFormData = {
  [stopId: string]: StopTimeFields;
};

interface OverrideStopCompletedTimesTabProps {
  selectedShipment: Shipment | undefined;
  onClose: () => void;
}

function OverrideStopCompletedTimesTab({
  selectedShipment,
  onClose,
}: OverrideStopCompletedTimesTabProps) {
  const { courierId } = useAuth();
  const { handleSubmit, setValue, control } =
    useForm<StopCompletedTimesFormData>();
  const [overrideStopCompletedTimesMutation, { loading }] =
    useOverrideStopCompletedTimesMutation();
  const { showToast } = useCourieStore();

  const stops = selectedShipment?.stops || [];
  const sortedStops = [...stops].sort(
    (a, b) => getFirstTaskSequence(a) - getFirstTaskSequence(b)
  );

  // Initialize the form only when the shipment data changes. Depending on
  // `sortedStops` here would re-run this on every parent re-render (it is a
  // new array each render) and wipe times the user has already entered.
  useEffect(() => {
    const shipmentStops = selectedShipment?.stops ?? [];
    shipmentStops.forEach((stop) => {
      if (stop.completedAt) {
        // Convert unix timestamp to moment in courier TZ, then to DatePicker/TimePicker Date
        const completedAtMoment = moment.unix(stop.completedAt);
        const completedAtDate = momentToTimePickerDate(completedAtMoment);
        setValue(`${stop.id}.date`, completedAtDate);
        setValue(`${stop.id}.time`, completedAtDate);
      } else {
        setValue(`${stop.id}.date`, null);
        setValue(`${stop.id}.time`, null);
      }
    });
  }, [selectedShipment, setValue]);

  const onSubmit = handleSubmit((data) => {
    if (!courierId || !selectedShipment?.id) return;

    const overrides = Object.entries(data)
      .filter(([stopId, fields]) => {
        const stop = stops.find((s) => s.id === stopId);
        return stop?.completedAt && fields.date && fields.time;
      })
      .map(([stopId, fields]) => {
        // Combine date and time and convert to unix timestamp in courier TZ
        // fields.date and fields.time are guaranteed non-null by the filter above
        const combinedDateTime = makeTimeOnDateInCourierTZ(
          fields.time!,
          fields.date!
        );

        return {
          stopId,
          completedAt: combinedDateTime,
        };
      });

    if (overrides.length === 0) {
      showToast({
        type: "error",
        message: "No completed stops to override",
      });
      return;
    }

    overrideStopCompletedTimesMutation({
      variables: {
        courierId,
        shipmentId: selectedShipment.id,
        overrides,
      },
    })
      .then(() => {
        showToast({
          type: "success",
          message: "Stop completion times updated successfully",
        });
        onClose();
      })
      .catch((error) => {
        showErrorToast(error, showToast);
      });
  });

  return (
    <form onSubmit={onSubmit}>
      <div className="mb-4">
        {/* Table Header */}
        <div className="grid grid-cols-[2fr,1fr,1fr] gap-4 mb-3 pb-2 border-b border-gray-300">
          <div className="font-semibold text-sm text-gray-700">Stop</div>
          <div className="font-semibold text-sm text-gray-700">
            Completed Date
          </div>
          <div className="font-semibold text-sm text-gray-700">
            Completed Time
          </div>
        </div>

        {/* Table Body */}
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {sortedStops.map((stop) => (
            <div
              key={stop.id}
              className={`grid grid-cols-[2fr,1fr,1fr] gap-4 items-start ${
                !stop.completedAt ? "opacity-50" : ""
              }`}
            >
              <div>
                <StopCard stop={stop} />
              </div>
              {stop.completedAt ? (
                <>
                  <div className="flex flex-col z-50">
                    <Controller
                      control={control}
                      name={`${stop.id}.date`}
                      render={({ field }) => (
                        <DatePicker
                          size="mini"
                          value={field.value}
                          formatString="MMM do, yyyy"
                          onChange={({ date }) => field.onChange(date)}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col z-40">
                    <Controller
                      control={control}
                      name={`${stop.id}.time`}
                      render={({ field }) => (
                        <TimePicker
                          size="mini"
                          nullable
                          value={dateToTimeString(field.value)}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            if (event.target.value === "") {
                              field.onChange(null);
                            } else {
                              field.onChange(
                                timeStringToDate(event.target.value)
                              );
                            }
                          }}
                        />
                      )}
                    />
                  </div>
                </>
              ) : (
                <div className="col-span-2 flex items-center">
                  <p className="text-sm text-gray-500">
                    Stop not completed yet
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <CourieButton onClick={onClose} color="secondary" type="button">
          Cancel
        </CourieButton>
        <CourieButton type="submit" disabled={loading} isProcessing={loading}>
          Override Stop Times
        </CourieButton>
      </div>
    </form>
  );
}

export { OverrideStopCompletedTimesTab };
export type { OverrideStopCompletedTimesTabProps };
